/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The shopping "settings" the family owns, each a small modal on the shared
 * live engine (docs/SHOPPING_LIST_10X.md §4.3–4.7, Phase B):
 *
 *   StoreSelect       where to buy something — optional, with the store the
 *                     family chose for it last time recommended
 *   StoresModal       the household's stores: add, rename, order, remove, and
 *                     each store's walking order of categories
 *   CategoriesModal   rename, re-icon, reorder, hide, add, remove
 *   ShareModal        a temporary link for someone outside the family
 *   PlanMergeModal    put planned days onto a list (Home by default)
 *   TemplatesModal    save a list as a template / add a template to a list
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { ShoppingApi } from "../../api/endpoints.ts";
import { ShoppingLiveApi } from "../../api/shoppingLive.ts";
import { addDays, formatDay, type YMD } from "../../lib/dates.ts";
import { Banner, ErrorBox, Modal } from "../../components/ui.tsx";
import { Icon, IconButton } from "../../components/Icon.tsx";
import { useToast } from "../../components/Toast.tsx";
import type { NewOp } from "./live/useShoppingLive.tsx";
import {
  homeListId, itemsOf, liveLists, sharesOf, storeName, templateLists, walkingOrder, type Model,
} from "./live/model.ts";

type Run = (ops: NewOp[], label: string | null) => void;

const STORE_ICONS = ["🏪", "🛒", "🏬", "🥖", "🥩", "🐟", "🍷", "💊", "🧺", "🌱"];

// ─────────────────────────────────────────────────────────────────────────────
// Where to buy it
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `value`: a store id, or null for "no particular store". `recommended` is
 * the store the household chose for this item last time, shown first.
 */
export function StoreSelect({ model, value, onChange, recommended, onAddStore, className = "" }: {
  model: Model;
  value: string | null;
  onChange: (storeId: string | null) => void;
  recommended?: string | null;
  onAddStore?: () => void;
  className?: string;
}) {
  return (
    <select className={`input ${className}`} value={value ?? ""} aria-label="Store"
      onChange={(e) => {
        if (e.target.value === "__new") { onAddStore?.(); return; }
        onChange(e.target.value || null);
      }}>
      <option value="">No particular store</option>
      {recommended && model.stores.some((s) => s.id === recommended) && (
        <option value={recommended}>★ {storeName(model, recommended)} (usual)</option>
      )}
      {model.stores.filter((s) => s.id !== recommended).map((s) => (
        <option key={s.id} value={s.id}>{s.icon ? `${s.icon} ` : ""}{s.name}</option>
      ))}
      {onAddStore && <option value="__new">+ Add a store…</option>}
    </select>
  );
}

/** One-field "add a store", used from any store picker. */
export function NewStoreModal({ run, onClose, onCreated }: {
  run: Run; onClose: () => void; onCreated?: (storeId: string) => void;
}) {
  const [name, setName] = useState("");
  const [icon, setIcon] = useState<string | null>("🏪");
  return (
    <Modal title="Add a store" onClose={onClose}>
      <form className="space-y-3" onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return;
        const storeId = crypto.randomUUID();
        run([{ op: "store.create", storeId, name: name.trim(), icon }], null);
        onCreated?.(storeId);
        onClose();
      }}>
        <input className="input" autoFocus value={name} maxLength={80}
          placeholder="Costco, Tesco Extra, the market…" onChange={(e) => setName(e.target.value)} />
        <IconPicker icons={STORE_ICONS} value={icon} onChange={setIcon} />
        <div className="flex gap-2">
          <button type="button" className="btn-ghost flex-1" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary flex-1" disabled={!name.trim()}>Add</button>
        </div>
      </form>
    </Modal>
  );
}

function IconPicker({ icons, value, onChange }: {
  icons: string[]; value: string | null; onChange: (i: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {icons.map((i) => (
        <button key={i} type="button" aria-pressed={value === i}
          className={`h-9 w-9 rounded-lg border text-lg transition ${
            value === i ? "border-accent bg-accent/15" : "border-rule-soft hover:bg-paper-3"}`}
          onClick={() => onChange(i)}>{i}</button>
      ))}
    </div>
  );
}

function move<T>(arr: T[], from: number, to: number): T[] {
  if (to < 0 || to >= arr.length) return arr;
  const next = [...arr];
  const [x] = next.splice(from, 1);
  next.splice(to, 0, x!);
  return next;
}

// ─────────────────────────────────────────────────────────────────────────────
// Stores
// ─────────────────────────────────────────────────────────────────────────────

export function StoresModal({ model, run, onClose }: { model: Model; run: Run; onClose: () => void }) {
  const [adding, setAdding] = useState(false);
  const [walking, setWalking] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const stores = model.stores;
  const counts = useMemo(() => {
    const c = new Map<string, number>();
    for (const i of Object.values(model.items)) if (i.store_id && i.status !== "got") c.set(i.store_id, (c.get(i.store_id) ?? 0) + 1);
    return c;
  }, [model.items]);

  if (walking) {
    return <WalkingOrderModal model={model} run={run} storeId={walking} onClose={() => setWalking(null)} />;
  }

  return (
    <Modal title="Stores" onClose={onClose}>
      <p className="mb-3 text-sm text-ink-3">
        Any item can say where to buy it. The store chosen for something is suggested the next
        time anyone adds it — never required.
      </p>
      <div className="card divide-y divide-rule-soft">
        {stores.length === 0 && <p className="p-3 text-sm text-ink-3">No stores yet.</p>}
        {stores.map((s, i) => (
          <div key={s.id} className="flex items-center gap-2 px-2 py-1.5 text-sm">
            <span className="w-6 text-center" aria-hidden>{s.icon ?? "🏪"}</span>
            <input key={s.name} className="input flex-1 py-1" defaultValue={s.name} aria-label={`Name of ${s.name}`}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v && v !== s.name) run([{ op: "store.update", storeId: s.id, name: v }], `Renamed to ${v}`);
              }} />
            <span className="hidden text-xs text-ink-3 sm:inline">{counts.get(s.id) ?? 0} to buy</span>
            <IconButton name="chevronDown" label={`Move ${s.name} down`} disabled={i === stores.length - 1}
              onClick={() => run([{ op: "store.reorder", storeIds: move(stores, i, i + 1).map((x) => x.id) }], null)} />
            <IconButton name="settings" label={`Walking order at ${s.name}`} onClick={() => setWalking(s.id)} />
            {confirming === s.id ? (
              <button className="btn-danger px-2 py-1" onClick={() => {
                run([{ op: "store.delete", storeId: s.id }], null); setConfirming(null);
              }}>Remove</button>
            ) : (
              <IconButton name="trash" tone="danger" label={`Remove ${s.name}`} onClick={() => setConfirming(s.id)} />
            )}
          </div>
        ))}
      </div>
      {confirming && (
        <p className="mt-2 text-xs text-ink-3">
          Removing a store leaves its items on their lists with no store.
        </p>
      )}
      <div className="mt-3 flex gap-2">
        <button className="btn-primary" onClick={() => setAdding(true)}>
          <Icon name="plus" className="h-4 w-4" /> Add a store
        </button>
        <button className="btn-ghost" onClick={onClose}>Done</button>
      </div>
      {adding && <NewStoreModal run={run} onClose={() => setAdding(false)} />}
    </Modal>
  );
}

function WalkingOrderModal({ model, run, storeId, onClose }: {
  model: Model; run: Run; storeId: string; onClose: () => void;
}) {
  const store = model.stores.find((s) => s.id === storeId);
  const [order, setOrder] = useState<string[]>(() => walkingOrder(model, storeId));
  const name = (k: string) => {
    const c = model.categories.find((x) => x.key === k);
    return c ? `${c.icon ? `${c.icon} ` : ""}${c.name}` : k;
  };
  return (
    <Modal title={`Walking order — ${store?.name ?? "store"}`} onClose={onClose}>
      <p className="mb-3 text-sm text-ink-3">
        The order you walk the aisles here. Shopping at this store follows it.
      </p>
      <ol className="card divide-y divide-rule-soft">
        {order.map((k, i) => (
          <li key={k} className="flex items-center gap-2 px-3 py-1 text-sm">
            <span className="w-6 text-ink-3">{i + 1}</span>
            <span className="flex-1">{name(k)}</span>
            <IconButton name="chevronDown" label={`Later: ${name(k)}`} disabled={i === order.length - 1}
              onClick={() => setOrder(move(order, i, i + 1))} />
            <IconButton name="chevronDown" className="rotate-180" label={`Earlier: ${name(k)}`} disabled={i === 0}
              onClick={() => setOrder(move(order, i, i - 1))} />
          </li>
        ))}
      </ol>
      <div className="mt-3 flex gap-2">
        <button className="btn-primary flex-1" onClick={() => {
          run([{ op: "store.update", storeId, aisleOrder: order }], "Walking order saved");
          onClose();
        }}>Save</button>
        <button className="btn-ghost" onClick={() => {
          run([{ op: "store.update", storeId, aisleOrder: [] }], "Back to the family's order");
          onClose();
        }}>Use the family's order</button>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Categories
// ─────────────────────────────────────────────────────────────────────────────

const CATEGORY_ICONS = ["🥦", "🥖", "🥩", "🐟", "🧀", "🥫", "🧁", "🥤", "🧊", "🧽", "🧴", "🍼", "💊", "🐾", "🕯️", "🍷", "🌶️", "🛒"];

export function CategoriesModal({ model, run, onClose }: { model: Model; run: Run; onClose: () => void }) {
  const [name, setName] = useState("");
  const [icon, setIcon] = useState<string | null>("🛒");
  const [iconFor, setIconFor] = useState<string | null>(null);
  const cats = model.categories;
  const movable = cats.filter((c) => c.key !== "other");

  return (
    <Modal title="Categories" onClose={onClose}>
      <p className="mb-3 text-sm text-ink-3">
        Your names and your order — lists group by these, and each store can walk them its own way.
      </p>
      <div className="card divide-y divide-rule-soft">
        {cats.map((c) => {
          const i = movable.findIndex((x) => x.key === c.key);
          return (
            <div key={c.key} className={`flex items-center gap-2 px-2 py-1.5 text-sm ${c.hidden ? "opacity-50" : ""}`}>
              <button className="h-8 w-8 rounded-lg border border-rule-soft text-base" title="Change icon"
                onClick={() => setIconFor(iconFor === c.key ? null : c.key)}>{c.icon ?? "·"}</button>
              <input key={c.name} className="input flex-1 py-1" defaultValue={c.name} aria-label={`Name of ${c.name}`}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v && v !== c.name) run([{ op: "category.update", key: c.key, name: v }], `Renamed to ${v}`);
                }} />
              {c.key !== "other" && (
                <>
                  <IconButton name="chevronDown" className="rotate-180" label={`Earlier: ${c.name}`} disabled={i <= 0}
                    onClick={() => run([{ op: "category.reorder", keys: move(movable, i, i - 1).map((x) => x.key) }], null)} />
                  <IconButton name="chevronDown" label={`Later: ${c.name}`} disabled={i === movable.length - 1}
                    onClick={() => run([{ op: "category.reorder", keys: move(movable, i, i + 1).map((x) => x.key) }], null)} />
                  <button className="text-xs text-ink-3 hover:text-ink"
                    onClick={() => run([{ op: "category.update", key: c.key, hidden: !c.hidden }],
                      c.hidden ? `Showing ${c.name}` : `Hid ${c.name}`)}>
                    {c.hidden ? "Show" : "Hide"}
                  </button>
                  <IconButton name="trash" tone="danger" label={`Remove ${c.name}`}
                    onClick={() => run([{ op: "category.delete", key: c.key }], `Removed ${c.name}`)} />
                </>
              )}
            </div>
          );
        })}
      </div>
      {iconFor && (
        <div className="mt-2">
          <IconPicker icons={CATEGORY_ICONS} value={cats.find((c) => c.key === iconFor)?.icon ?? null}
            onChange={(i) => { run([{ op: "category.update", key: iconFor, icon: i }], null); setIconFor(null); }} />
        </div>
      )}
      <form className="mt-4 space-y-2" onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return;
        run([{ op: "category.create", name: name.trim(), icon }], `Added ${name.trim()}`);
        setName("");
      }}>
        <div className="flex gap-2">
          <input className="input" placeholder="New category — Kosher butcher, Garden…" value={name}
            onChange={(e) => setName(e.target.value)} />
          <button className="btn-primary" disabled={!name.trim()}>Add</button>
        </div>
        <IconPicker icons={CATEGORY_ICONS} value={icon} onChange={setIcon} />
      </form>
      <p className="mt-3 text-xs text-ink-3">
        Removing one you made moves its items to Other; built-in ones are hidden instead.
      </p>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Temporary links
// ─────────────────────────────────────────────────────────────────────────────

const DURATIONS: Array<{ label: string; hours: number }> = [
  { label: "1 day", hours: 24 }, { label: "3 days", hours: 72 },
  { label: "1 week", hours: 168 }, { label: "2 weeks", hours: 336 }, { label: "30 days", hours: 720 },
];

export function shareUrl(token: string): string {
  return `${window.location.origin}/s/${token}`;
}

export function ShareModal({ model, run, listId, onClose }: {
  model: Model; run: Run; listId: string; onClose: () => void;
}) {
  const toast = useToast();
  const [label, setLabel] = useState("Grandma");
  const [hours, setHours] = useState(168);
  const shares = sharesOf(model, listId);
  const list = model.lists[listId];

  const copy = async (token: string) => {
    try {
      await navigator.clipboard.writeText(shareUrl(token));
      toast("Link copied.", "ok");
    } catch {
      toast("Couldn't copy — select the link and copy it.", "danger");
    }
  };

  return (
    <Modal title={`Share "${list?.title ?? "list"}"`} onClose={onClose}>
      <p className="mb-3 text-sm text-ink-3">
        For someone outside the family: they see this one list live, can add and tick, without an
        account — and only until the link runs out or you switch it off.
      </p>
      {shares.length > 0 && (
        <div className="card mb-4 divide-y divide-rule-soft">
          {shares.map((s) => (
            <div key={s.id} className="space-y-1.5 p-3 text-sm">
              <div className="flex items-center justify-between gap-2">
                <strong>{s.label}</strong>
                <span className="text-xs text-ink-3">until {new Date(s.expires_at).toLocaleString()}</span>
              </div>
              <input className="input py-1 text-xs" readOnly value={shareUrl(s.token)}
                onFocus={(e) => e.target.select()} aria-label={`Link for ${s.label}`} />
              <div className="flex flex-wrap gap-2">
                <button className="btn-ghost px-2 py-1" onClick={() => void copy(s.token)}>
                  <Icon name="copy" className="h-4 w-4" /> Copy
                </button>
                <button className="btn-ghost px-2 py-1"
                  onClick={() => run([{ op: "share.extend", shareId: s.id, hours: 168 }], "Extended a week")}>
                  +1 week
                </button>
                <button className="btn-danger px-2 py-1"
                  onClick={() => run([{ op: "share.revoke", shareId: s.id }], null)}>
                  Switch off
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <form className="space-y-3" onSubmit={(e) => {
        e.preventDefault();
        if (!label.trim()) return;
        run([{ op: "share.create", listId, shareId: crypto.randomUUID(), label: label.trim(), hours }], null);
        toast("Link made — copy it below.", "ok");
      }}>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-sm">Who's it for?</span>
            <input className="input mt-1" value={label} maxLength={60} onChange={(e) => setLabel(e.target.value)} />
          </label>
          <label className="block">
            <span className="text-sm">For how long?</span>
            <select className="input mt-1" value={hours} onChange={(e) => setHours(Number(e.target.value))}>
              {DURATIONS.map((d) => <option key={d.hours} value={d.hours}>{d.label}</option>)}
            </select>
          </label>
        </div>
        <button className="btn-primary w-full" disabled={!label.trim()}>
          <Icon name="link" className="h-4 w-4" /> Make a link
        </button>
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// The plan, onto a list
// ─────────────────────────────────────────────────────────────────────────────

export function PlanMergeModal({ model, today, initialListId, onClose }: {
  model: Model; today: YMD; initialListId?: string; onClose: () => void;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const [from, setFrom] = useState<YMD>(today);
  const [to, setTo] = useState<YMD>(addDays(today, 6));
  const [target, setTarget] = useState<string>(initialListId ?? homeListId(model) ?? "home");
  const [applyStaples, setApplyStaples] = useState(true);
  const [applyInventory, setApplyInventory] = useState(false);
  const lists = liveLists(model);

  const merge = useMutation({
    mutationFn: async () => {
      if (target === "__new") {
        const r = await ShoppingApi.generate({ from, to, applyStaples, applyInventory });
        return { kind: "new" as const, listId: r.list.id, count: r.items.length };
      }
      const r = await ShoppingLiveApi.mergePlan({ listId: target, from, to, applyStaples, applyInventory });
      return { kind: "merge" as const, ...r };
    },
    onSuccess: (r) => {
      if (r.kind === "new") {
        if (!r.count) { toast("Nothing is planned for those days.", "danger"); return; }
        toast(`${r.count} items on a new list.`, "ok");
      } else if (!r.dates.length) {
        toast(r.alreadyCovered.length ? "Those days are already on that list." : "Nothing to add.", "neutral");
        return;
      } else {
        toast(`${r.added} added, ${r.topped} topped up on ${r.title}.`, "ok");
      }
      onClose();
      navigate(`/shopping/${r.listId}`);
    },
  });

  return (
    <Modal title="Add the meal plan to a list" onClose={onClose}>
      {merge.error && <ErrorBox message={(merge.error as Error).message} />}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className="text-sm">From</span>
            <input type="date" className="input mt-1" value={from} onChange={(e) => setFrom(e.target.value as YMD)} /></label>
          <label className="block"><span className="text-sm">To</span>
            <input type="date" className="input mt-1" value={to} onChange={(e) => setTo(e.target.value as YMD)} /></label>
        </div>
        <label className="block"><span className="text-sm">Onto</span>
          <select className="input mt-1" value={target} onChange={(e) => setTarget(e.target.value)}>
            {lists.map((l) => <option key={l.id} value={l.id}>{l.icon ? `${l.icon} ` : ""}{l.title}</option>)}
            <option value="__new">A new list just for these days</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={applyStaples} onChange={(e) => setApplyStaples(e.target.checked)} />
          Skip cupboard staples
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={applyInventory} onChange={(e) => setApplyInventory(e.target.checked)} />
          Subtract what's already in the pantry
        </label>
        <p className="text-xs text-ink-3">
          {formatDay(from)} – {formatDay(to)}. Days already on the list aren't added twice; if the plan
          changes later, the list says so and shows you the differences.
        </p>
        <button className="btn-primary w-full" disabled={merge.isPending} onClick={() => merge.mutate()}>
          {merge.isPending ? "Working it out…" : "Add to the list"}
        </button>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Templates
// ─────────────────────────────────────────────────────────────────────────────

export function SaveTemplateModal({ model, run, listId, onClose }: {
  model: Model; run: Run; listId: string; onClose: () => void;
}) {
  const list = model.lists[listId];
  const [title, setTitle] = useState(list ? (list.kind === "home" ? "Weekly basics" : list.title) : "");
  const n = itemsOf(model, listId).filter((i) => i.status !== "got").length;
  return (
    <Modal title="Save as a template" onClose={onClose}>
      <form className="space-y-3" onSubmit={(e) => {
        e.preventDefault();
        if (!title.trim()) return;
        run([{ op: "list.saveTemplate", listId, templateId: crypto.randomUUID(), title: title.trim() }],
          `Saved "${title.trim()}"`);
        onClose();
      }}>
        <p className="text-sm text-ink-3">
          The {n} item{n === 1 ? "" : "s"} still needed on this list become a template — "Weekly basics",
          "Shabbat", "Hosting 6" — that you can add to any list in one go.
        </p>
        <input className="input" autoFocus value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
        <button className="btn-primary w-full" disabled={!title.trim() || n === 0}>Save template</button>
      </form>
    </Modal>
  );
}

export function TemplatesModal({ model, run, listId, onClose }: {
  model: Model; run: Run; listId: string; onClose: () => void;
}) {
  const templates = templateLists(model);
  const [confirming, setConfirming] = useState<string | null>(null);
  return (
    <Modal title="Add from a template" onClose={onClose}>
      {templates.length === 0 ? (
        <p className="text-sm text-ink-3">
          No templates yet. Open any list and choose "Save as a template".
        </p>
      ) : (
        <div className="card divide-y divide-rule-soft">
          {templates.map((t) => {
            const items = itemsOf(model, t.id);
            return (
              <div key={t.id} className="flex items-center gap-2 p-3 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{t.icon ? `${t.icon} ` : ""}{t.title}</div>
                  <div className="truncate text-xs text-ink-3">{items.map((i) => i.display_name).join(", ")}</div>
                </div>
                <button className="btn-primary px-2 py-1"
                  onClick={() => { run([{ op: "template.apply", templateId: t.id, listId }], null); onClose(); }}>
                  Add {items.length}
                </button>
                {confirming === t.id ? (
                  <button className="btn-danger px-2 py-1"
                    onClick={() => { run([{ op: "list.delete", listId: t.id }], null); setConfirming(null); }}>
                    Delete
                  </button>
                ) : (
                  <IconButton name="trash" tone="danger" label={`Delete ${t.title}`} onClick={() => setConfirming(t.id)} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Usuals — bought on a rhythm, offered when due, never added without a tap
// ─────────────────────────────────────────────────────────────────────────────

export function UsualsStrip({ listId, model, onAdd }: {
  listId: string; model: Model; onAdd: (op: NewOp, label: string) => void;
}) {
  const usuals = useQuery({ queryKey: ["shopping", "usuals"], queryFn: () => ShoppingLiveApi.usuals(), staleTime: 60_000 });
  const needed = new Set(itemsOf(model, listId).filter((i) => i.status !== "got").map((i) => i.display_name.toLowerCase()));
  const items = (usuals.data?.items ?? []).filter((u) => !needed.has(u.name.toLowerCase()));
  const [hidden, setHidden] = useState(false);
  useEffect(() => { setHidden(false); }, [listId]);
  if (!items.length || hidden) return null;
  return (
    <Banner onDismiss={() => setHidden(true)}>
      <span className="mr-1">Usually due:</span>
      {items.map((u) => (
        <button key={u.name} className="chip-toggle mr-1.5 mt-1"
          title={`About every ${u.everyDays} day${u.everyDays === 1 ? "" : "s"}; last ${u.daysSince} days ago`}
          onClick={() => onAdd({
            op: "item.add", name: u.name, aisle: u.aisle ?? undefined,
            quantity: u.quantity ?? undefined, unit: u.unit ?? undefined,
            ...(u.storeId ? { storeId: u.storeId } : {}),
          }, `Added ${u.name}`)}>
          <Icon name="plus" className="h-3 w-3" /> {u.name}
        </button>
      ))}
    </Banner>
  );
}
